/**
 * Display-label helpers for active filter chips/tooltips.
 *
 * Lives in its own module (not co-located with FilterChip) so the component
 * file only exports components — keeping React Fast Refresh happy.
 */

import type { FilterCondition } from '@/lib/crm/filters/types';
import { formatAssociationName } from '@/lib/crm/list/utils/getColumnLabel';

/**
 * Best-effort human label for a target property name when no schema label is
 * available (the list surfaces only load the SOURCE entity's property labels,
 * not the association target's). Strips a leading `ahoy_`, spaces underscores,
 * and title-cases — e.g. `ahoy_company_type` → "Company Type".
 */
export function humanizeTargetProperty(name: string): string {
  const spaced = name
    .replace(/^ahoy_/, '')
    .replace(/_/g, ' ')
    .trim();
  return spaced.replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Display label for a filter condition across all three shapes:
 * - property → the property's label
 * - association (by id) → the association's label
 * - associated-property (target_property set) → "<assoc> · <Target Field>"
 */
export function getFilterConditionDisplayLabel(
  condition: Pick<FilterCondition, 'field' | 'association' | 'target_property'>,
  associationLabels: Record<string, string>,
  propertyLabels: Record<string, string>
): string {
  if (condition.association) {
    const assocLabel =
      associationLabels[condition.association] ||
      formatAssociationName(condition.association);
    if (condition.target_property) {
      return `${assocLabel} · ${humanizeTargetProperty(condition.target_property)}`;
    }
    return assocLabel;
  }
  return (
    propertyLabels[condition.field] || humanizeTargetProperty(condition.field)
  );
}
